/**
 * Landing page targeting outpatient nutrition practices (Praxis).
 *
 * Public MVP landing page. Klinik-specific routes are intentionally not
 * promoted in the public rollout.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ClipboardList,
  Users,
  Euro,
  ShieldCheck,
  Sparkles,
  CalendarCheck,
  FileSpreadsheet,
  MessageSquare,
  Smartphone,
  CheckCircle2,
  Lock,
  Menu,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const VALUE_PROPS = [
  {
    icon: ClipboardList,
    title: 'GNCP-Dokumentation',
    text: 'Strukturierte Dokumentation nach German Nutrition Care Process mit klarer manueller Freigabe für Diagnose, Intervention und Monitoring.',
  },
  {
    icon: Users,
    title: 'Patientenportal',
    text: 'Ernährungstagebuch, Aufgaben, Dokumente und sichere Nachrichten – aktive Patient:innen zwischen den Terminen.',
  },
  {
    icon: Euro,
    title: '§ 125 & § 43 Abrechnung',
    text: 'Kassenrezepte, GKV-, PKV- und Selbstzahler-Workflows in einem Tool – inklusive Erinnerungen für Heilmittel-Verordnungen.',
  },
  {
    icon: ShieldCheck,
    title: 'DSGVO & EU-Hosting',
    text: 'Server in Frankfurt, Audit-Trails auf jeder Patientenakte, BSI-konforme Session-Sicherheit und Verarbeitungsverzeichnis auf Knopfdruck.',
  },
] as const;

const WORKFLOW_STEPS = [
  {
    icon: CalendarCheck,
    title: 'Online-Buchung statt Telefonliste',
    text: 'Eigene Buchungsseite pro Praxis. Patient:innen wählen Termin, Sie bestätigen mit einem Klick.',
  },
  {
    icon: Sparkles,
    title: 'Dokumentenablage für Befunde',
    text: 'Befunde und Dokumente zentral ablegen, wiederfinden und in der Akte nachvollziehbar dokumentieren.',
  },
  {
    icon: ClipboardList,
    title: 'Dokumentation in Minuten',
    text: 'Vorlagen, Sprach-Notizen und KI-Zusammenfassungen verkürzen die Nachbereitung pro Termin spürbar.',
  },
  {
    icon: FileSpreadsheet,
    title: 'Rechnung in einem Schritt',
    text: 'Aus dem Termin direkt zur PDF – inklusive GOÄ-Ziffern, § 125-Position oder Selbstzahler-Layout.',
  },
  {
    icon: Smartphone,
    title: 'Offline-fähige PWA',
    text: 'Beim Hausbesuch oder im Funkloch: Daten werden lokal gespeichert und synchronisieren später.',
  },
  {
    icon: MessageSquare,
    title: 'Patientenkommunikation, sortiert',
    text: 'Nachrichten, Dokumente und Aufgaben sammeln sich an einem Ort statt in mehreren Posteingängen.',
  },
] as const;

const TRUST_ITEMS = [
  'EU-Hosting (Supabase Frankfurt)',
  'BSI-konformes Session-Timeout',
  'Vollständiges DSGVO-Verzeichnis',
  'KI ohne Patientennamen (Pseudonymisierung)',
  '72-Stunden-Breach-Workflow',
] as const;

const FAQ_ITEMS = [
  {
    q: 'Kann ich meine bestehenden Patient:innen importieren?',
    a: 'Ja. Sie können Stammdaten per CSV importieren oder einzelne Akten manuell anlegen. Bestehende Dokumente lassen sich pro Patient:in hochladen und werden in der Akte versioniert abgelegt.',
  },
  {
    q: 'Funktioniert die Abrechnung mit Krankenkassen?',
    a: 'Ja. § 125 SGB V (Heilmittelverordnung), § 43 SGB V (Patientenschulungen), PKV und Selbstzahler werden unterstützt. PDF-Rechnungen sind GOBD-konform und enthalten alle notwendigen Pflichtangaben.',
  },
  {
    q: 'Wer hat Zugriff auf meine Daten?',
    a: 'Nur Sie und die Personen, denen Sie in Ihrer Praxis explizit Rollen zuweisen. Alle Daten liegen pseudonymisiert in der EU. KI-Anfragen werden vor dem Versand serverseitig pseudonymisiert – Patientennamen verlassen Ihre Praxis nie im Klartext.',
  },
  {
    q: 'Was passiert, wenn ich kündige?',
    a: 'Sie können jederzeit Ihre vollständigen Daten als Export anfordern (Patientenakten, Dokumente, Rechnungen) und diese in andere Systeme überführen. Nach Kündigung werden Ihre Daten DSGVO-konform gelöscht.',
  },
  {
    q: 'Ist die KI als Medizinprodukt gedacht?',
    a: 'Nein. Icarus D&D ist im öffentlichen MVP als Dokumentations- und Verwaltungsassistent positioniert. Die KI strukturiert Texte, bereitet Entwürfe vor und hilft beim Wiederfinden von Informationen. Sie gibt keine Diagnose-, Therapie- oder Risikoempfehlungen; die fachliche Verantwortung bleibt bei Ihnen.',
  },
  {
    q: 'Kann ich Icarus D&D vor dem Kauf testen?',
    a: 'Ja. Es gibt einen 14-tägigen Testzugang ohne Kreditkarte sowie einen interaktiven Demo-Modus mit anonymisierten Beispieldaten unter /demo.',
  },
] as const;

const LandingPraxis = () => {
  // F-02-12: mobile hamburger menu
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* F-02-05: Skip-to-Content link (WCAG 2.1 SC 2.4.1) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-card focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-medium"
      >
        Zum Inhalt springen
      </a>
      <header className="border-b border-border/40">
        <div className="max-w-6xl mx-auto flex items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/icarus_logo_600px.webp"
              alt="Icarus D&D"
              className="w-7 h-7 object-contain"
            />
            <span className="font-semibold tracking-tight">Icarus D&D</span>
          </Link>
          <nav className="hidden md:flex gap-2">
            <Link to="/pricing">
              <Button variant="ghost" size="sm">
                Preise
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" size="sm">
                Anmelden
              </Button>
            </Link>
          </nav>
          <button
            className="md:hidden p-2 rounded-xl text-muted-foreground hover:bg-secondary transition-colors"
            onClick={() => setMenuOpen(v => !v)}
            aria-expanded={menuOpen}
            aria-label="Menü öffnen"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {/* Mobile nav panel */}
        {menuOpen && (
          <div className="md:hidden border-t border-border/40 px-4 py-3 space-y-1 bg-background">
            <Link to="/pricing" className="block px-3 py-2.5 text-sm font-medium rounded-xl hover:bg-secondary" onClick={() => setMenuOpen(false)}>
              Preise
            </Link>
            <Link to="/login" className="block px-3 py-2.5 text-sm font-medium rounded-xl hover:bg-secondary" onClick={() => setMenuOpen(false)}>
              Anmelden
            </Link>
          </div>
        )}
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="py-24 px-4 text-center max-w-3xl mx-auto space-y-6">
        <span className="inline-block rounded-full bg-primary/10 text-primary text-xs font-medium px-3 py-1">
          Für Ernährungs-Praxen
        </span>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
          Mehr Zeit für Patient:innen.
          <br className="hidden sm:block" /> Weniger Verwaltung.
        </h1>
        <p className="text-lg text-muted-foreground">
          GNCP-Dokumentation, Teletherapie, GKV-Abrechnung und ein modernes Patientenportal in einer
          Plattform - ohne Excel, ohne Zettel, ohne Datenwildwuchs.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-3 pt-2">
          <Link to="/login/ambulant">
            <Button size="lg" className="w-full sm:w-auto">
              14 Tage kostenlos starten
              <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </Link>
          <Link to="/demo">
            <Button variant="outline" size="lg" className="w-full sm:w-auto">
              Demo ansehen
            </Button>
          </Link>
        </div>
        <p className="text-xs text-muted-foreground pt-2">
          Keine Kreditkarte nötig · Daten in der EU · DSGVO-konform
        </p>
      </section>

      {/* ── Value Props ──────────────────────────────────────────────── */}
      <section className="py-16 px-4 max-w-5xl mx-auto">
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {VALUE_PROPS.map(({ icon: Icon, title, text }) => (
            <article
              key={title}
              className="bg-card rounded-2xl border border-border p-6 space-y-3"
            >
              <Icon className="w-5 h-5 text-primary" aria-hidden="true" />
              <h3 className="font-semibold">{title}</h3>
              <p className="text-sm text-muted-foreground">{text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── Workflow / „So spart Icarus D&D Zeit" ─────────────────── */}
      <section className="py-20 px-4 border-y border-border/40 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
            <h2 className="text-3xl font-bold tracking-tight">
              So sieht ein Praxistag mit Icarus D&D aus
            </h2>
            <p className="text-muted-foreground">
              Sechs konkrete Stellen, an denen die Plattform den Verwaltungsaufwand
              reduziert – damit der Termin wieder im Mittelpunkt steht.
            </p>
          </div>
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {WORKFLOW_STEPS.map(({ icon: Icon, title, text }) => (
              <article
                key={title}
                className="bg-card rounded-2xl border border-border p-6 space-y-3"
              >
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-primary" aria-hidden="true" />
                </div>
                <h3 className="font-semibold">{title}</h3>
                <p className="text-sm text-muted-foreground">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── DSGVO / Datenschutz ──────────────────────────────────────── */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto grid gap-10 lg:grid-cols-2 lg:items-center">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary text-xs font-medium px-3 py-1">
              <Lock className="w-3 h-3" />
              Datenschutz by design
            </div>
            <h2 className="text-3xl font-bold tracking-tight">
              Patientendaten gehören Ihnen – und in die EU.
            </h2>
            <p className="text-muted-foreground">
              Wir hosten ausschließlich in Frankfurt, verschlüsseln Patientendaten
              vor dem Speichern und trennen Identifikatoren strikt von medizinischen
              Inhalten. KI-Anfragen werden serverseitig pseudonymisiert, bevor sie
              den europäischen Mistral-Endpunkt erreichen.
            </p>
            <div className="pt-2">
              <Link to="/datenschutz">
                <Button variant="outline" size="sm">
                  Datenschutzerklärung lesen
                </Button>
              </Link>
            </div>
          </div>
          <ul className="bg-card rounded-2xl border border-border p-6 space-y-3">
            {TRUST_ITEMS.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm">
                <CheckCircle2
                  className="w-4 h-4 text-primary mt-0.5 shrink-0"
                  aria-hidden="true"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <section className="py-20 px-4 border-t border-border/40 bg-muted/30">
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="text-center space-y-2">
            <h2 className="text-3xl font-bold tracking-tight">Häufige Fragen</h2>
            <p className="text-muted-foreground">
              Antworten auf das, was Praxen vor der Entscheidung wissen wollen.
            </p>
          </div>
          <dl className="space-y-4">
            {FAQ_ITEMS.map(({ q, a }) => (
              <details
                key={q}
                className="group bg-card rounded-2xl border border-border p-5 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-3 font-semibold">
                  <span>{q}</span>
                  <ArrowRight
                    className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90"
                    aria-hidden="true"
                  />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  {a}
                </p>
              </details>
            ))}
          </dl>
        </div>
      </section>

      {/* ── Sekundärer CTA ───────────────────────────────────────────── */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center bg-card rounded-3xl border border-border p-10 space-y-5">
          <h2 className="text-3xl font-bold tracking-tight">
            Bereit für eine strukturierte Praxis?
          </h2>
          <p className="text-muted-foreground">
            Testen Sie Icarus D&D 14 Tage kostenlos - mit allen Funktionen und
            ohne Kreditkarte.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 pt-2">
            <Link to="/login/ambulant">
              <Button size="lg" className="w-full sm:w-auto">
                Kostenlos starten
                <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
            <a href="mailto:info@icarus-health.de?subject=D%3AE%3AF%3AY%20-%20Beratungstermin%20Praxis">
              <Button variant="outline" size="lg" className="w-full sm:w-auto">
                Beratungstermin
              </Button>
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-border/40 mt-16 py-8 px-4 text-sm text-muted-foreground">
        <div className="max-w-6xl mx-auto flex flex-wrap gap-x-6 gap-y-2 justify-between">
          <span>© {new Date().getFullYear()} Icarus Health</span>
          <nav className="flex gap-4">
            <Link to="/impressum" className="hover:text-foreground">
              Impressum
            </Link>
            <Link to="/datenschutz" className="hover:text-foreground">
              Datenschutz
            </Link>
            <Link to="/agb" className="hover:text-foreground">
              AGB
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default LandingPraxis;
